/**
 * Virtualized Message List Component
 * Implements virtual scrolling for performance with large message lists
 * Requirements: 7.1, 8.5
 */

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import MessageItem from "./MessageItem";
import SkeletonLoader from "../common/SkeletonLoader";

const VirtualizedMessageList = ({
  messages = [],
  loading = false,
  hasMore = false,
  onLoadMore,
  onLikeMessage,
  onReplyToMessage,
  currentUserId,
  highlightMessageId,
  highlightReplyId,
  containerHeight = 600,
  itemHeight = 120, // Estimated height per message
  overscan = 5, // Number of items to render outside visible area
  className = "",
  ...props
}) => {
  const [scrollTop, setScrollTop] = useState(0);
  const [containerSize, setContainerSize] = useState({
    width: 0,
    height: containerHeight,
  });
  const [itemHeights, setItemHeights] = useState(new Map());

  const containerRef = useRef(null);
  const scrollElementRef = useRef(null);
  // const measurementCache = useRef(new Map()); // Available for future height caching optimizations
  const resizeObserver = useRef(null);

  // Calculate visible range
  const visibleRange = useMemo(() => {
    if (messages.length === 0) {
      return { start: 0, end: 0 };
    }

    let accumulatedHeight = 0;
    let startIndex = 0;
    let endIndex = messages.length - 1;

    // Find start index
    for (let i = 0; i < messages.length; i++) {
      const height = itemHeights.get(messages[i]._id) || itemHeight;
      if (accumulatedHeight + height > scrollTop) {
        startIndex = Math.max(0, i - overscan);
        break;
      }
      accumulatedHeight += height;
    }

    // Find end index
    accumulatedHeight = 0;
    for (let i = 0; i < messages.length; i++) {
      const height = itemHeights.get(messages[i]._id) || itemHeight;
      accumulatedHeight += height;
      if (accumulatedHeight > scrollTop + containerSize.height) {
        endIndex = Math.min(messages.length - 1, i + overscan);
        break;
      }
    }

    return { start: startIndex, end: endIndex };
  }, [
    messages,
    scrollTop,
    containerSize.height,
    itemHeight,
    overscan,
    itemHeights,
  ]);

  // Calculate total height and item positions
  const { totalHeight, itemPositions } = useMemo(() => {
    let height = 0;
    const positions = new Map();

    messages.forEach((message, index) => {
      positions.set(message._id, height);
      const messageHeight = itemHeights.get(message._id) || itemHeight;
      height += messageHeight;
    });

    return { totalHeight: height, itemPositions: positions };
  }, [messages, itemHeights, itemHeight]);

  // Visible messages
  const visibleMessages = useMemo(() => {
    return messages.slice(visibleRange.start, visibleRange.end + 1);
  }, [messages, visibleRange]);

  // Auto-scroll to highlighted message when messages load
  useEffect(() => {
    if (highlightMessageId && messages.length > 0) {
      // Small delay to ensure DOM is updated
      const timer = setTimeout(() => {
        const messageElement = document.getElementById(
          `message-${highlightMessageId}`
        );
        if (messageElement && containerRef.current) {
          messageElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [highlightMessageId, messages.length]);

  // Handle scroll events
  const handleScroll = useCallback(
    (event) => {
      const newScrollTop = event.target.scrollTop;
      setScrollTop(newScrollTop);

      // Load more messages when near bottom
      if (hasMore && !loading && onLoadMore) {
        const scrollHeight = event.target.scrollHeight;
        const clientHeight = event.target.clientHeight;
        const scrollPosition = newScrollTop + clientHeight;

        // Load more when 200px from bottom
        if (scrollHeight - scrollPosition < 200) {
          onLoadMore();
        }
      }
    },
    [hasMore, loading, onLoadMore]
  );

  // Measure item height when rendered
  const measureItem = useCallback(
    (messageId, element) => {
      if (!element || !messageId) return;

      const height = element.getBoundingClientRect().height;
      if (height > 0 && height !== itemHeights.get(messageId)) {
        setItemHeights((prev) => new Map(prev).set(messageId, height));
      }
    },
    [itemHeights]
  );

  // Setup resize observer for container
  useEffect(() => {
    if (!containerRef.current) return;

    resizeObserver.current = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerSize({ width, height });
      }
    });

    resizeObserver.current.observe(containerRef.current);

    return () => {
      if (resizeObserver.current) {
        resizeObserver.current.disconnect();
      }
    };
  }, []);

  // Scroll to top when messages change (new messages added)
  useEffect(() => {
    if (scrollElementRef.current && messages.length > 0) {
      // Only auto-scroll if user is near the top (new messages)
      if (scrollTop < 100) {
        scrollElementRef.current.scrollTop = 0;
      }
    }
  }, [messages.length, scrollTop]);

  // Performance monitoring
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      console.log(
        `📊 VirtualizedMessageList: Rendering ${visibleMessages.length}/${messages.length} messages`
      );
    }
  }, [visibleMessages.length, messages.length]);

  const renderMessage = useCallback(
    (message, index) => {
      const position = itemPositions.get(message._id) || 0;
      const actualIndex = visibleRange.start + index;

      return (
        <div
          key={message._id}
          style={{
            position: "absolute",
            top: position,
            left: 0,
            right: 0,
            minHeight: itemHeight,
          }}
          ref={(el) => measureItem(message._id, el)}
        >
          <MessageItem
            message={message}
            onLike={onLikeMessage}
            onReply={onReplyToMessage}
            currentUserId={currentUserId}
            index={actualIndex}
            isVirtualized={true}
            isHighlighted={highlightMessageId === message._id}
            highlightReplyId={highlightReplyId}
          />
        </div>
      );
    },
    [
      itemPositions,
      visibleRange.start,
      itemHeight,
      measureItem,
      onLikeMessage,
      onReplyToMessage,
      currentUserId,
      highlightMessageId,
      highlightReplyId,
    ]
  );

  if (messages.length === 0 && !loading) {
    return (
      <div
        className={`flex items-center justify-center h-64 text-gray-500 ${className}`}
      >
        <div className="text-center">
          <p className="text-lg font-medium">No messages yet</p>
          <p className="text-sm">Be the first to start the discussion!</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden ${className}`}
      style={{ height: containerHeight }}
      {...props}
    >
      <div
        ref={scrollElementRef}
        className="absolute inset-0 overflow-auto"
        onScroll={handleScroll}
        style={{ height: "100%" }}
      >
        {/* Virtual scrolling container */}
        <div
          style={{
            height: totalHeight,
            position: "relative",
          }}
        >
          {/* Render visible messages */}
          {visibleMessages.map((message, index) =>
            renderMessage(message, index)
          )}
        </div>

        {/* Loading indicator */}
        {loading && (
          <div className="p-4">
            <SkeletonLoader type="message" count={3} />
          </div>
        )}

        {/* Load more indicator */}
        {hasMore && !loading && (
          <div className="p-4 text-center">
            <button
              onClick={onLoadMore}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Load more messages
            </button>
          </div>
        )}

        {/* End of messages indicator */}
        {!hasMore && messages.length > 0 && (
          <div className="p-4 text-center text-gray-500 text-sm">
            You've reached the beginning of the conversation
          </div>
        )}
      </div>

      {/* Scroll indicators */}
      {messages.length > 10 && (
        <>
          {/* Scroll to top button */}
          {scrollTop > 200 && (
            <button
              onClick={() => {
                if (scrollElementRef.current) {
                  scrollElementRef.current.scrollTop = 0;
                }
              }}
              className="absolute top-4 right-4 bg-blue-600 text-white p-2 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-10"
              title="Scroll to top"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 10l7-7m0 0l7 7m-7-7v18"
                />
              </svg>
            </button>
          )}

          {/* Scroll to bottom button */}
          {scrollTop < totalHeight - containerSize.height - 200 && (
            <button
              onClick={() => {
                if (scrollElementRef.current) {
                  scrollElementRef.current.scrollTop = totalHeight;
                }
              }}
              className="absolute bottom-4 right-4 bg-blue-600 text-white p-2 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-10"
              title="Scroll to bottom"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 14l-7 7m0 0l-7-7m7 7V3"
                />
              </svg>
            </button>
          )}
        </>
      )}

      {/* Performance debug info (development only) */}
      {process.env.NODE_ENV === "development" && (
        <div className="absolute top-2 left-2 bg-black bg-opacity-75 text-white text-xs p-2 rounded">
          <div>
            Visible: {visibleMessages.length}/{messages.length}
          </div>
          <div>
            Range: {visibleRange.start}-{visibleRange.end}
          </div>
          <div>Scroll: {Math.round(scrollTop)}px</div>
          <div>Height: {Math.round(totalHeight)}px</div>
        </div>
      )}
    </div>
  );
};

export default VirtualizedMessageList;
